import { createContext, useCallback, useContext, useEffect, useState } from "react";
import { api, getToken, setToken } from "./api";
import AuthGateModal from "../components/AuthGateModal";

const Ctx = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [gate, setGate] = useState({ open: false, message: null });

  const refresh = useCallback(async () => {
    const t = getToken();
    if (!t) {
      setUser(null);
      return;
    }
    try {
      const me = await api.me();
      setUser(me);
    } catch (e) {
      // Only clear the token if the server explicitly rejected it (401/403).
      // Network blips / aborted fetches shouldn't log a valid user out.
      if (e?.status === 401 || e?.status === 403) {
        setToken(null);
        setUser(null);
      }
    }
  }, []);

  useEffect(() => {
    (async () => {
      await refresh();
      setLoading(false);
    })();
  }, [refresh]);

  const login = async (email, password) => {
    const r = await api.login(email, password);
    setToken(r.token);
    setUser(r.user);
  };

  const register = async (email, password, name, referralCode) => {
    const r = await api.register(email, password, name, referralCode);
    setToken(r.token);
    setUser(r.user);
  };

  const logout = async () => {
    setToken(null);
    setUser(null);
  };

  // Run the action if logged in, otherwise pop the sign-up modal.
  const requireAuth = useCallback((action, message) => {
    if (user) {
      action?.();
      return true;
    }
    setGate({ open: true, message: message || null });
    return false;
  }, [user]);

  const openAuthGate = useCallback((message) => {
    setGate({ open: true, message: message || null });
  }, []);
  const closeAuthGate = useCallback(() => setGate({ open: false, message: null }), []);

  return (
    <Ctx.Provider value={{
      user, loading, login, register, logout, refresh,
      requireAuth, openAuthGate, closeAuthGate,
    }}>
      {children}
      <AuthGateModal
        open={gate.open}
        message={gate.message}
        onClose={closeAuthGate}
      />
    </Ctx.Provider>
  );
}

export function useAuth() {
  const v = useContext(Ctx);
  if (!v) throw new Error("useAuth must be used inside AuthProvider");
  return v;
}
